import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { CheckCircle, Package, ArrowRight, TrendingUp, Truck, Mail, MapPin, Clock, Building2, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PledgeConfirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [container, setContainer] = useState(null);
  
  // Support both pledgeData and orderData from different flows
  const pledgeData = location.state?.pledgeData;
  const orderData = location.state?.orderData;
  const paymentMethod = location.state?.paymentMethod;
  const isPending = location.state?.isPending;

  useEffect(() => {
    // Redirect if no data
    if (!pledgeData && !orderData) {
      navigate('/products');
      return;
    }

    // Fetch updated container status
    const fetchContainer = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/containers/current`);
        setContainer(response.data);
      } catch (error) {
        console.error('Error fetching container:', error);
      }
    };

    fetchContainer();
  }, [pledgeData, orderData, navigate]);

  if (!pledgeData && !orderData) {
    return null;
  }

  // Handle wire/check pending orders
  if (isPending && orderData) {
    const isWire = paymentMethod === 'wire';
    const isCheck = paymentMethod === 'check';
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />
        
        <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="order-confirmation-page">
          <div className="max-w-2xl mx-auto">
            {/* Success Icon */}
            <div className="text-center mb-8 animate-fade-in">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                {isWire ? (
                  <Building2 className="w-12 h-12 text-blue-600" />
                ) : (
                  <Mail className="w-12 h-12 text-blue-600" />
                )}
              </div>
              <h1 className="font-bold text-4xl md:text-5xl tracking-tight leading-tight mb-4" data-testid="confirmation-title">
                Thank You for Your Order!
              </h1>
              <p className="text-xl text-gray-600">
                {isWire 
                  ? 'Wire transfer instructions have been sent to your email'
                  : 'Check mailing instructions have been sent to your email'}
              </p>
            </div>

            {/* Order Details Card */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100 mb-8">
              <div className="flex items-center mb-6">
                <Package className="w-6 h-6 text-primary mr-3" />
                <h2 className="text-2xl font-bold">Your Order Details</h2>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-600">Order ID:</span>
                  <span className="font-mono font-semibold">{orderData.order_id}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-600">Payment Method:</span>
                  <span className="font-semibold">{isWire ? 'Wire Transfer' : 'Check by Mail'}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-100 bg-primary/5 -mx-4 px-4 rounded-lg">
                  <span className="text-primary font-semibold">Amount Due:</span>
                  <span className="font-bold text-primary text-xl">${orderData.total_amount?.toFixed(2)}</span>
                </div>
                {orderData.remaining_balance > 0 && (
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-600">Balance Due Later (70%):</span>
                    <span className="font-semibold">${orderData.remaining_balance?.toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* 72-Hour Notice */}
              <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-bold text-amber-800">72-Hour Payment Window</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Your container space is reserved for 72 hours. Please complete your {isWire ? 'wire transfer' : 'check payment'} before the deadline to secure your order.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Instructions */}
            <div className="bg-blue-50 rounded-3xl p-8 border border-blue-100 mb-8">
              <h3 className="font-bold text-xl mb-4">
                {isWire ? 'Wire Transfer Instructions' : 'Check Mailing Instructions'}
              </h3>
              
              {isWire ? (
                <div className="space-y-4">
                  <p className="text-gray-700">
                    We've sent detailed bank account information to <strong>{orderData.email}</strong>. 
                    Please check your email (including spam folder) for complete wire transfer instructions.
                  </p>
                  <div className="p-4 bg-white rounded-xl border border-blue-200">
                    <p className="text-sm text-gray-600 mb-1">Reference/Memo:</p>
                    <p className="font-mono font-bold text-lg">{orderData.order_id}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                      <div>
                        <p className="font-bold text-red-800">Cashier's / Official Check ONLY</p>
                        <p className="text-sm text-red-700">Personal checks will not be accepted.</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-xl border border-blue-200">
                    <p className="text-sm text-gray-600 mb-2">Mail your check to:</p>
                    <p className="font-semibold">BlueCrate Group</p>
                    <p>8 The Green STE B</p>
                    <p>Dover, DE 19901</p>
                    <p className="mt-2 text-sm text-gray-600">Memo: <span className="font-mono font-bold">{orderData.order_id}</span></p>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/my-orders" className="flex-1">
                <Button className="w-full h-12 rounded-full bg-primary hover:bg-primary/90" data-testid="view-orders-button">
                  Track My Order
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/" className="flex-1">
                <Button variant="outline" className="w-full h-12 rounded-full border-2" data-testid="back-home-button">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Original confirmation page for Stripe payments
  const containerFillPercentage = container 
    ? ((container.current_fill / container.total_capacity) * 100).toFixed(1)
    : 0;

  // Determine if this is an immediate shipment (LCL) or shared container (FCL)
  const isImmediateShipment = pledgeData?.immediateShipment || pledgeData?.shipping_method === 'lcl';
  const shippingMethod = pledgeData?.shipping_method || 'fcl';

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="pledge-confirmation-page">
        <div className="max-w-2xl mx-auto">
          {/* Success Icon */}
          <div className="text-center mb-8 animate-fade-in">
            <div className={`w-24 h-24 ${isImmediateShipment ? 'bg-orange-100' : 'bg-green-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
              {isImmediateShipment ? (
                <Truck className="w-12 h-12 text-orange-600" />
              ) : (
                <CheckCircle className="w-12 h-12 text-green-600" />
              )}
            </div>
            <h1 className="font-bold text-4xl md:text-5xl tracking-tight leading-tight mb-4" data-testid="confirmation-title">
              {isImmediateShipment ? 'Order Confirmed - Shipping Now!' : 'Pledge Confirmed!'}
            </h1>
            <p className="text-xl text-gray-600">
              {isImmediateShipment 
                ? 'Your order will ship within 3-5 business days'
                : 'You\'ll be notified when the container is ready to ship'}
            </p>
          </div>

          {/* Pledge Details Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100 mb-8">
            <div className="flex items-center mb-6">
              <Package className="w-6 h-6 text-primary mr-3" />
              <h2 className="text-2xl font-bold">Your Pledge Details</h2>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600">Product:</span>
                <span className="font-semibold">{pledgeData.product_name}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600">Quantity:</span>
                <span className="font-bold text-primary text-xl">{parseInt(pledgeData.sqft).toLocaleString()} sqft</span>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600">Product Cost:</span>
                <span className="font-semibold">${parseFloat(pledgeData.total_price).toFixed(2)}</span>
              </div>
              
              {isImmediateShipment ? (
                <>
                  <div className="flex justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-600">Shipping (LCL Direct):</span>
                    <span className="font-semibold">${pledgeData.shippingCost ? parseFloat(pledgeData.shippingCost).toFixed(2) : '0.00'}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-100 bg-orange-50 -mx-4 px-4 rounded-lg">
                    <span className="text-orange-700 font-semibold">Total Paid (100%):</span>
                    <span className="font-bold text-orange-700 text-xl">${pledgeData.totalAmount ? parseFloat(pledgeData.totalAmount).toFixed(2) : parseFloat(pledgeData.total_price).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-3">
                    <span className="text-gray-600">Shipping Method:</span>
                    <span className="font-semibold text-orange-600">Direct Ship (LCL)</span>
                  </div>
                </>
              ) : (
                <>
                  {pledgeData.depositAmount && (
                    <>
                      <div className="flex justify-between py-3 border-b border-gray-100 bg-green-50 -mx-4 px-4 rounded-lg">
                        <span className="text-green-700 font-semibold">Deposit Paid (10%):</span>
                        <span className="font-bold text-green-700">${pledgeData.depositAmount}</span>
                      </div>
                      <div className="flex justify-between py-3 border-b border-gray-100">
                        <span className="text-gray-600">Remaining Balance (90%):</span>
                        <span className="font-semibold">${pledgeData.remainingBalance}</span>
                      </div>
                      <div className="flex justify-between py-3">
                        <span className="text-gray-600">Due When:</span>
                        <span className="font-semibold">Container is full</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between py-3">
                    <span className="text-gray-600">Shipping Method:</span>
                    <span className="font-semibold text-primary">Shared Container (FCL)</span>
                  </div>
                </>
              )}
              
              {pledgeData.shipping_address && (
                <div className="flex items-start py-3 border-t border-gray-200 mt-4">
                  <MapPin className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Delivery Address</p>
                    <p className="text-sm text-gray-600">
                      {pledgeData.shipping_address.street}<br />
                      {pledgeData.shipping_address.city}, {pledgeData.shipping_address.state} {pledgeData.shipping_address.zip_code}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Container Update Visual - Only for FCL */}
            {!isImmediateShipment && container && (
              <div className="mt-8 p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">Container #20491 Updated!</span>
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-600">Filled:</span>
                  <span className="text-lg font-bold text-primary">
                    {container.current_fill.toLocaleString()} / {container.total_capacity.toLocaleString()} SQFT
                  </span>
                </div>
                <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary to-blue-500 rounded-full transition-all duration-1000"
                    style={{ width: `${containerFillPercentage}%` }}
                  ></div>
                </div>
                <p className="text-center text-sm font-bold text-primary mt-2">
                  {containerFillPercentage}% Full
                </p>
              </div>
            )}
          </div>

          {/* Next Steps - Different for FCL vs LCL */}
          <div className={`${isImmediateShipment ? 'bg-orange-50 border-orange-100' : 'bg-blue-50 border-blue-100'} rounded-3xl p-8 border mb-8`}>
            <h3 className="font-bold text-xl mb-4">What Happens Next?</h3>
            
            {isImmediateShipment ? (
              // LCL (Ship Now) Instructions
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-orange-200 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <Mail className="w-3 h-3 text-orange-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Email Receipt & PO</p>
                    <p className="text-sm text-gray-700">You'll receive an email within 15 minutes with your purchase order receipt and invoice for your records.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-orange-200 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <Clock className="w-3 h-3 text-orange-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Order Processing (3-5 Business Days)</p>
                    <p className="text-sm text-gray-700">Your order will be prepared and shipped directly from our supplier within 3-5 business days.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-orange-200 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <Truck className="w-3 h-3 text-orange-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Shipment Tracking</p>
                    <p className="text-sm text-gray-700">Once shipped, you'll receive tracking information via email including Bill of Lading, container number, and estimated delivery date (~30 days total).</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-orange-200 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <MapPin className="w-3 h-3 text-orange-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Delivery Coordination</p>
                    <p className="text-sm text-gray-700">Our team will contact you 5-7 days before arrival to schedule delivery to your address or arrange pickup from the port.</p>
                  </div>
                </div>
                
                {/* Contact Info */}
                <div className="mt-6 p-4 bg-white rounded-xl border border-orange-200">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Questions about your shipment?</p>
                  <p className="text-sm text-gray-700">
                    Email: <a href="mailto:shipping@bluecrategroup.com" className="text-orange-600 font-semibold hover:underline">shipping@bluecrategroup.com</a><br />
                    Phone: <span className="font-semibold">(555) 123-4567</span>
                  </p>
                </div>
              </div>
            ) : (
              // FCL (Shared Container) Instructions
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-xs font-bold text-primary">1</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">10% Deposit Confirmed</p>
                    <p className="text-sm text-gray-700">Your deposit has been processed and your place in Container #20491 is secured. No further action needed at this time.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-xs font-bold text-primary">2</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Container Filling (2-6 Weeks)</p>
                    <p className="text-sm text-gray-700">We'll monitor the container fill status. Current fill: {container ? `${containerFillPercentage}%` : 'Loading...'}. You can track progress on your dashboard.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-xs font-bold text-primary">3</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Final Payment Request</p>
                    <p className="text-sm text-gray-700">When the container is nearly full, you'll receive an invoice for the remaining 90% balance (${pledgeData.remainingBalance || '0.00'}). Payment due within 7 days.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-xs font-bold text-primary">4</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Shipping & Tracking</p>
                    <p className="text-sm text-gray-700">Once the container ships (~18-30 days transit), you'll receive tracking details, ETA, and delivery scheduling options.</p>
                  </div>
                </div>
                
                {/* Contact Info */}
                <div className="mt-6 p-4 bg-white rounded-xl border border-blue-200">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Need help or have questions?</p>
                  <p className="text-sm text-gray-700">
                    Email: <a href="mailto:hello@bluecrategroup.com" className="text-primary font-semibold hover:underline">hello@bluecrategroup.com</a><br />
                    We respond within 24 hours Monday-Friday
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/dashboard" className="flex-1">
              <Button className="w-full h-12 rounded-full bg-primary hover:bg-primary/90" data-testid="view-dashboard-button">
                View Dashboard
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/" className="flex-1">
              <Button variant="outline" className="w-full h-12 rounded-full border-2" data-testid="back-home-button">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PledgeConfirmation;
